/* eslint-disable react/prop-types */

import React, { useState } from 'react';
import Creatable from 'react-select/creatable';
import { components } from 'react-select';

import { validEmail } from 'Shared/helpers';
import { useClientSideSearchData } from 'Shared/utils/hooks';
import useValidateEmployeeEmail from 'Shared/utils/hooks/use-validate-employee-email';
import { useOrganization } from 'Shared/utils/hooks/use-organization';

import MultiValueContainer from './MultiValueContainer/MultiValueContainer';
import CustomCollaboratorsMenuList from './CustomCollaboratorsMenuList/CustomCollaboratorsMenuList';
import WpInlineError from 'Wpds/components/inline-error/WpInlineError';
import WpIcon from 'Wpds/components/icon/WpIcon';

import './InviteCollaboratorsInput.scss';

const inviteeStyles = ({ byEmail }) => ({
  menuPortal: base => ({ ...base, zIndex: 9999 }),
  multiValue: (provided, state) => {
    const isInvalidEmail = byEmail && !validEmail(state.data.value);

    if (isInvalidEmail) {
      return {
        ...provided,
        backgroundColor: '#f8d7da',
      };
    }

    return {
      ...provided,
      backgroundColor: '#ABC3FD',
      borderRadius: '4px',
    };
  },
  multiValueLabel: base => ({
    ...base,
    color: '#0238BB',
  }),
  multiValueRemove: base => ({
    ...base,
    color: '#0238BB',
    marginLeft: 'auto',
  }),
  clearIndicator: base => ({
    ...base,
    color: '#B6BAC4',
  }),
});

const InvitCollaboratorsInputLabel = () => (
  <>
    <b>Invite collaborators using their work email</b>
    <span className="text-muted ml-1">(optional)</span>
  </>
);

interface Props {
  isMulti?: boolean;
  byEmail?: boolean;
  label?: React.ReactNode;
  placeholder?: string;
  value: Array<{
    label: string;
    value: string;
  }>;
  options?: Array<{
    label: string;
    value: string;
  }>;
  isValidateEmailDomain?: boolean;
  noOptionsMessage?: string;
  loading?: boolean;
  onChange: Function;
}

function isFullArray(array) {
  if (!array) return false;
  const filteredArray = array.filter(n => n);
  return filteredArray && filteredArray.length;
}

const getEmailFormatErrorMessage = emails => {
  if (!isFullArray(emails)) return null;
  const invalidEmails = emails.filter(({ value: email }) => !validEmail(email));
  if (invalidEmails && invalidEmails.length)
    return `Invalid email address: ${invalidEmails.map(({ value: v }) => v).join(',')}`;
  return null;
};

const getEmailDomainsErrorMessage = (emails, availableDomains) => {
  if (!isFullArray(emails)) return null;
  const invalidEmails = emails.filter(({ value: email }) => !availableDomains.includes(email.replace(/.*@/, '')));

  if (invalidEmails.length) {
    const invalidDomainMessage = `Emails with invalid domains: ${invalidEmails.map(({ value: v }) => v).join(',')}`;
    const availableDomainsMessage = `Allowed domains: ${availableDomains.join(',')}`;
    return `${invalidDomainMessage}. ${availableDomainsMessage}`;
  }

  return null;
};

function InviteCollaboratorsInput({
  value = [],
  isMulti = true,
  byEmail = true,
  onChange,
  label = <InvitCollaboratorsInputLabel />,
  placeholder = '',
  options,
  isValidateEmailDomain = false,
  noOptionsMessage = '',
  loading,
}: Props) {
  const [errorMessage, setErrorMessage] = useState(null);
  const [errorDomainsMessage, setErrorDomainsMessage] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const { clientSideSearchData, isLoading } = useClientSideSearchData();
  const { organization, isPiOrganization } = useOrganization();
  const { isEmployeeEmailOrValidDomain } = useValidateEmployeeEmail();
  const availableDomains = isValidateEmailDomain ? organization.availableDomains : [];

  const getPiEmailDomainsErrorMessage = emails => {
    if (!isFullArray(emails)) return null;
    const invalidPiDomains = emails.filter(({ value: email }) => !isEmployeeEmailOrValidDomain({ email }));

    if (invalidPiDomains.length) {
      const invalidDomainMessage = `Emails with invalid PI domains: ${invalidPiDomains
        .map(({ value: v }) => v)
        .join(',')}`;
      const availableDomainsMessage = `Allowed domains: ${organization.pi_allowed_domains.join(', ')}`;
      return `${invalidDomainMessage}. ${availableDomainsMessage}`;
    }
  };

  const validateEmailValues = emails => {
    const emailValue = Array.isArray(emails) ? emails : [emails];
    const invalidFormatEmailMessage = getEmailFormatErrorMessage(emailValue);

    const invalidDomainEmailMessage = isValidateEmailDomain
      ? getEmailDomainsErrorMessage(emailValue, availableDomains)
      : null;
    setErrorDomainsMessage(invalidDomainEmailMessage);

    const invalidPIEmailDomainMessage = isPiOrganization ? getPiEmailDomainsErrorMessage(emailValue) : null;

    if (invalidFormatEmailMessage) return setErrorMessage(invalidFormatEmailMessage);
    return setErrorMessage(invalidPIEmailDomainMessage);
  };

  const handleChange = (values, opt) => {
    if (isMulti && !values) {
      setErrorMessage(null);
      setErrorDomainsMessage(null);
      return onChange([]);
    }
    if (opt?.action === 'select-option')
      return setTimeout(() => {
        onChange(values);
        if (byEmail) validateEmailValues(values);
      });
    if (byEmail) validateEmailValues(values);
    return onChange(values);
  };

  const handleInputChange = fieldValue => {
    setInputValue(fieldValue);
    if (fieldValue && fieldValue.length > 1) {
      const lastChar = fieldValue[fieldValue.length - 1];
      const emailString = fieldValue.slice(0, fieldValue.length - 1);
      const selectObject = [...(value || []), { label: emailString, value: emailString, __isNew__: true }];
      if (!lastChar.match(/[0-9a-zA-Z@./+-]/)) {
        if (byEmail) validateEmailValues(selectObject);
        onChange(selectObject);
        setInputValue('');
      }
    }
  };

  const handleBlur = event => {
    const typedValue = event.target.value;
    if (!typedValue) return null;
    const onBlurValue = isMulti
      ? [...(value || []), { label: typedValue, value: typedValue, __isNew__: true }]
      : { label: typedValue, value: typedValue, __isNew__: true };
    return handleChange(onBlurValue, { action: 'blur' });
  };

  const collaboratorsOptions = clientSideSearchData
    .filter(({ coworker }) => coworker)
    .map(({ email }) => ({ label: email || '', value: email || '' }));

  return (
    <div className="w-100">
      <div className="invite-collaborators-input-header text-muted">{label}</div>
      <div className="input-group p2">
        <Creatable
          isClearable
          className="w-100"
          classNamePrefix="invite-collaborators-form-input"
          menuPlacement="bottom"
          styles={inviteeStyles({ byEmail })}
          isMulti={isMulti}
          isLoading={loading ?? isLoading}
          value={value}
          inputValue={inputValue}
          onInputChange={handleInputChange}
          placeholder={placeholder}
          formatCreateLabel={input => input}
          options={options ?? collaboratorsOptions}
          onChange={handleChange}
          onBlur={handleBlur}
          noOptionsMessage={() => noOptionsMessage}
          menuPosition={'absolute'}
          menuPortalTarget={document.body}
          components={{
            MultiValueContainer,
            MultiValueRemove: props => (
              <components.MultiValueRemove {...props}>
                <div
                  className="invite-collaborators-input-remove"
                  role="button"
                  data-test="InviteCollaboratorsInput-remove-invitee-button"
                >
                  <WpIcon name="times-circle" />
                </div>
              </components.MultiValueRemove>
            ),
            DropdownIndicator: () => null,
            ClearIndicator: props => (
              <components.ClearIndicator {...props}>
                <div role="button" data-test="InviteCollaboratorsInput-clear-all-button">
                  <WpIcon name="times-circle" />
                </div>
              </components.ClearIndicator>
            ),
            IndicatorSeparator: () => null,
            Menu: ({ children, ...props }) =>
              byEmail ? <components.Menu {...props}>{children}</components.Menu> : null,
            MenuList: ({ children }) =>
              byEmail ? <CustomCollaboratorsMenuList>{children}</CustomCollaboratorsMenuList> : null,
          }}
        />
        {errorMessage && <WpInlineError error={errorMessage} />}
        {errorDomainsMessage && <WpInlineError error={errorDomainsMessage} />}
      </div>
    </div>
  );
}

export default InviteCollaboratorsInput;
